import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { forumAPI } from '../services/api';
import ReactQuillEditor from '../components/ReactQuillEditor';
import GuestAuthBanner from '../components/GuestAuthBanner';
import {
  MessageSquare,
  Heart,
  Share2,
  Send,
  Image as ImageIcon,
  X,
  Loader2,
  Clock,
  ArrowLeft,
  Plus,
  Search,
  ChevronDown
} from 'lucide-react';

interface User {
  _id: string;
  name: string;
  email: string;
  role?: string;
}

interface Post {
  _id: string;
  author: User;
  content: string;
  images?: string[];
  tags?: string[];
  category?: string;
  likes: Array<{ user: string | User; createdAt: string }>;
  comments: Array<{ user: string | User; content: string; createdAt: string; _id: string }>;
  shares?: Array<{ user: string | User; createdAt: string }>;
  isEdited?: boolean;
  createdAt: string;
  updatedAt: string;
}

const CategoryPosts: React.FC = () => {
  const { categoryName } = useParams<{ categoryName: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [postImages, setPostImages] = useState<string[]>([]);
  const [postTags, setPostTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'popular' | 'commented'>('newest');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const filteredPosts = useMemo(() => {
    let result = [...posts];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        (p.content || '').toLowerCase().includes(q) ||
        (p.author?.name || '').toLowerCase().includes(q) ||
        (p.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }
    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'popular':
        result.sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0));
        break;
      case 'commented':
        result.sort((a, b) => (b.comments?.length || 0) - (a.comments?.length || 0));
        break;
    }
    return result;
  }, [posts, searchQuery, sortBy]);

  const totalLikes = useMemo(() => posts.reduce((sum, p) => sum + (p.likes?.length || 0), 0), [posts]);
  const totalComments = useMemo(() => posts.reduce((sum, p) => sum + (p.comments?.length || 0), 0), [posts]);

  useEffect(() => {
    if (categoryName) {
      setCurrentPage(1);
      loadPosts(1);
    }
  }, [categoryName]);

  const loadPosts = async (page: number = currentPage) => {
    if (!categoryName) return;
    setLoading(true);
    setError(null);
    try {
      const decoded = decodeURIComponent(categoryName);
      const response = await forumAPI.getPosts({ page, limit: 20, category: decoded });
      if (response.data.success) {
        setPosts(response.data.data);
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.pages);
          setCurrentPage(response.data.pagination.page);
        }
      } else setError(response.data.message || 'Failed to load posts');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    Array.from(files).forEach((file) => {
      if (file.size > 5 * 1024 * 1024) { setError('Image too large (max 5MB)'); return; }
      const reader = new FileReader();
      reader.onloadend = () => setPostImages(prev => [...prev, reader.result as string]);
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreatePost = async () => {
    const textContent = postContent.replace(/<[^>]*>/g, '').trim();
    if (!textContent && postImages.length === 0) { setError('Please add content or images'); return; }
    if (!user) { setError('You must be logged in'); return; }
    if (!categoryName) { setError('Category not found'); return; }

    setCreating(true);
    setError(null);
    try {
      const response = await forumAPI.createPost({
        content: postContent,
        images: postImages,
        tags: postTags,
        category: decodeURIComponent(categoryName)
      });
      if (response.data.success) {
        setPostContent('');
        setPostImages([]);
        setPostTags([]);
        setTagInput('');
        setShowCreatePost(false);
        loadPosts(currentPage);
      } else setError(response.data.message || 'Failed to create post');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create post');
    } finally {
      setCreating(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !postTags.includes(tagInput.trim())) {
      setPostTags([...postTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => setPostTags(postTags.filter(t => t !== tag));

  const handleLike = async (postId: string) => {
    if (!user) { setError('You must be logged in to like'); return; }
    try {
      const response = await forumAPI.likePost(postId);
      if (response.data.success) loadPosts(currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to like');
    }
  };

  const formatTimeAgo = (date: string) => {
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const isLiked = (post: Post) => {
    if (!user) return false;
    return post.likes.some(like => {
      if (!like?.user) return false;
      const likeUserId = typeof like.user === 'object' ? like.user._id : like.user;
      return likeUserId === user._id;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading posts...</p>
        </div>
      </div>
    );
  }

  const decodedCategoryName = categoryName ? decodeURIComponent(categoryName) : '';

  const getThreadTitle = (post: Post) => {
    const text = String(post.content || '').replace(/<[^>]*>/g, '').trim();
    return !text ? 'New post' : text.length > 80 ? `${text.slice(0, 80)}…` : text;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <GuestAuthBanner message="Sign up or log in to create posts, comment, and engage with the community!" />

        {/* Category Header */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate('/forum')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{decodedCategoryName}</h1>
                <p className="text-gray-500 mt-1">{posts.length} {posts.length === 1 ? 'post' : 'posts'} in this category</p>
              </div>
            </div>
            {user && (
              <button onClick={() => setShowCreatePost(!showCreatePost)} className="inline-flex items-center px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium shadow-sm">
                <Plus className="h-4 w-4 mr-2" /> Create Post
              </button>
            )}
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-50 rounded-lg p-2.5 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-emerald-600" />
              <div>
                <p className="text-sm font-bold text-gray-900">{posts.length}</p>
                <p className="text-[11px] text-gray-500">Posts</p>
              </div>
            </div>
            <div className="bg-rose-50 rounded-lg p-2.5 flex items-center gap-2">
              <Heart className="h-4 w-4 text-rose-500" />
              <div>
                <p className="text-sm font-bold text-gray-900">{totalLikes}</p>
                <p className="text-[11px] text-gray-500">Likes</p>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-2.5 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-sm font-bold text-gray-900">{totalComments}</p>
                <p className="text-[11px] text-gray-500">Comments</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Sort Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts in this category..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm"
            />
          </div>
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'newest' | 'oldest' | 'popular' | 'commented')}
              className="appearance-none pl-4 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm cursor-pointer"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="popular">Most Liked</option>
              <option value="commented">Most Discussed</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <div className="p-1 bg-red-100 rounded-lg">
              <X className="h-4 w-4 text-red-600" />
            </div>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {showCreatePost && user && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-sm">
                  <span className="text-sm font-bold text-white">{user.name?.charAt(0).toUpperCase()}</span>
                </div>
              </div>
              <div className="flex-1">
                <ReactQuillEditor value={postContent} onChange={setPostContent} placeholder="What's on your mind?" className="bg-white rounded-lg" style={{ minHeight: '200px' }} />
                <div className="mt-3">
                  <div className="flex items-center space-x-2">
                    <input type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} placeholder="Add tags (press Enter)" className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500" />
                    <button onClick={addTag} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 text-sm">Add</button>
                  </div>
                  {postTags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {postTags.map((tag, idx) => (
                        <span key={idx} className="inline-flex items-center px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm border border-emerald-200">
                          #{tag}
                          <button onClick={() => removeTag(tag)} className="ml-2 hover:text-emerald-900"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {postImages.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {postImages.map((image, idx) => (
                      <div key={idx} className="relative group">
                        <img src={image} alt={`Preview ${idx + 1}`} className="w-full h-32 object-cover rounded-lg" />
                        <button onClick={() => setPostImages(prev => prev.filter((_, i) => i !== idx))} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between mt-4">
                  <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" multiple className="hidden" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center text-gray-500 hover:text-emerald-600 transition-colors">
                    <ImageIcon className="h-5 w-5 mr-2" /> Photo
                  </button>
                  <div className="flex gap-3">
                    <button onClick={() => { setShowCreatePost(false); setPostContent(''); setPostTags([]); setPostImages([]); }} className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
                    <button onClick={handleCreatePost} disabled={creating || (!postContent.replace(/<[^>]*>/g, '').trim() && postImages.length === 0)} className="px-5 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center text-sm font-medium shadow-sm">
                      {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Posting...</> : <><Send className="h-4 w-4 mr-2" /> Post</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {filteredPosts.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchQuery ? 'No matching posts' : 'No posts yet'}
            </h3>
            <p className="text-gray-500">
              {user ? (searchQuery ? 'Try adjusting your search query' : 'Be the first to share something in this category!') : 'Login to create and view posts'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPosts.map((post) => {
              return (
                <div key={post._id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
                  {/* Post Header */}
                  <div
                    className="p-5 cursor-pointer"
                    onClick={() => navigate(`/forum/post/${post._id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <span className="text-sm font-bold text-white">{post.author?.name?.charAt(0).toUpperCase() || '?'}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">{post.author?.name || 'Unknown User'}</span>
                          {post.author?.role === 'admin' && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">Admin</span>
                          )}
                          {post.isEdited && <span className="text-xs text-gray-400">(edited)</span>}
                          <span className="text-xs text-gray-400 ml-auto flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTimeAgo(post.createdAt)}
                          </span>
                        </div>
                        <p className="text-gray-700 line-clamp-2 text-sm">{getThreadTitle(post)}</p>
                        {post.tags?.length ? (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {post.tags.slice(0, 4).map((tag, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-xs border border-emerald-200">#{tag}</span>
                            ))}
                            {post.tags.length > 4 && <span className="text-xs text-gray-400">+{post.tags.length - 4} more</span>}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* Post Stats Row */}
                    <div className="flex items-center gap-4 mt-3 ml-13">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Heart className={`h-3.5 w-3.5 ${isLiked(post) ? 'text-rose-500 fill-current' : ''}`} />
                        {post.likes?.length || 0}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <MessageSquare className="h-3.5 w-3.5" />
                        {post.comments?.length || 0}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Share2 className="h-3.5 w-3.5" />
                        {post.shares?.length || 0}
                      </span>
                      <span className="ml-auto text-xs text-emerald-600 font-medium">
                        Click to view →
                      </span>
                    </div>
                  </div>

                  </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => { setCurrentPage(prev => Math.max(1, prev - 1)); loadPosts(Math.max(1, currentPage - 1)); }}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .reduce((acc: (number | string)[], page, idx, arr) => {
                  if (idx > 0 && (page as number) - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  typeof item === 'string' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => { setCurrentPage(item); loadPosts(item); }}
                      className={`w-9 h-9 rounded-xl text-sm font-medium transition-colors ${currentPage === item ? 'bg-emerald-600 text-white shadow-sm' : 'text-gray-700 hover:bg-gray-100'}`}
                    >
                      {item}
                    </button>
                  )
                )
              }
            </div>
            <button
              onClick={() => { setCurrentPage(prev => Math.min(totalPages, prev + 1)); loadPosts(Math.min(totalPages, currentPage + 1)); }}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              Next
            </button>
          </div>
        )}

        </div>
    </div>
  );
};

export default CategoryPosts;
